import { match } from "ts-pattern";

import { fetchWithAuth } from "~/common/fetch";
import { isSignedOfferRequiredError } from "~/common/query";
import {
  API_DELIVERIES,
  API_DELIVERY_PICKUP,
  API_DELIVERY_PICKUP_BY_PHONE,
  API_DELIVERY_SHIPMENT,
  API_SHIPMENTS,
} from "~/config";
import type { TUserActionNotification } from "~/reducers/dashboard";
import type { AppDispatch } from "~/store";
import type { TDelivery } from "~/types/delivery";
import type { TPatient } from "~/types/patient";

import { getPatientId } from "./get_patient_id";
import { addUserActionNotification } from "./user_notification";

export enum DeliveryDestination {
  LABORATORY = "laboratory",
  CLINIC = "clinic",
}
export const COURIER_PHONE_NUMBER = "+7 (495) 961-22-11";

export const GET_DELIVERIES = "GET_DELIVERIES";
export const GET_DELIVERIES_ERROR = "GET_DELIVERIES_ERROR";
export const GET_DELIVERIES_SUCCESS = "GET_DELIVERIES_SUCCESS";
export const UPDATE_DELIVERY_INFO = "UPDATE_DELIVERY_INFO";
export const CREATE_SHIPMENT = "CREATE_SHIPMENT";
export const CREATE_SHIPMENT_ERROR = "CREATE_SHIPMENT_ERROR";
export const CREATE_SHIPMENT_SUCCESS = "CREATE_SHIPMENT_SUCCESS";
export const PICKUP = "PICKUP";
export const PICKUP_ERROR = "PICKUP_BY_ERROR";
export const PICKUP_SUCCESS = "PICKUP_BY_SUCCESS";

export const UPS_TRACK_LOCALE_MAP = {
  ru: "ru_RU",
  en: "en_US",
} as const;

const E_INVALID_FROM = "E_INVALID_FROM";
const E_UNHANDLED_ERROR = "E_UNHANDLED_ERROR";
const E_SERVICE_UNAVAILABLE = "E_SERVICE_UNAVAILABLE";

const E_HANDLED_ERRORS = [E_INVALID_FROM, E_UNHANDLED_ERROR, E_SERVICE_UNAVAILABLE];

type DeliveryErrorActionType = typeof CREATE_SHIPMENT_ERROR | typeof PICKUP_ERROR;
type DeliveryErrorPayload = {
  error?: string;
};

export const SERVICE_UPS = "UPS";
export const SERVICE_ME = "Major Express";

type UPSLocale = (typeof UPS_TRACK_LOCALE_MAP)[keyof typeof UPS_TRACK_LOCALE_MAP];

/** Return a carrier tracking URL, or `undefined` so unknown carriers render as plain text. */
export function trackingLink(
  tracking: TDelivery["track_number"],
  _type: string | null,
  locale: UPSLocale | null = null,
): string | undefined {
  return match(_type)
    .with(
      SERVICE_UPS,
      () => `https://www.ups.com/track?loc=${locale}&tracknum=${tracking}&requester=WT/`,
    )
    .with(
      SERVICE_ME,
      () =>
        `https://www.major-express.ru/useful-services/track/?type=1&tracking-number=${tracking}&smart-token=`,
    )
    .otherwise(() => undefined);
}

// Returns a MESSAGE ID, not a free string — the notification's `message` is the localized-id union, and
// typing this as `string` made every call site an error.
/**
 * Rejection carrying the failed `Response`.
 *
 * `parseDeliveryError` reads the response body through `json()`. This subclass preserves that contract
 * while providing an Error stack.
 */
class DeliveryRequestError extends Error {
  constructor(readonly response: Response) {
    super(`Delivery request failed with status ${response.status}`);
    this.name = "DeliveryRequestError";
  }

  json(): Promise<unknown> {
    return this.response.json();
  }
}

function getDeliveryErrorMessage(payload: DeliveryErrorPayload): TUserActionNotification["message"] {
  const key = typeof payload.error == "string" && E_HANDLED_ERRORS.includes(payload.error) ? payload.error : E_SERVICE_UNAVAILABLE;
  return `pat.deliveries.error.${key}` as TUserActionNotification["message"];
}

async function parseDeliveryError(error: unknown): Promise<DeliveryErrorPayload> {
  if (typeof error != "object" || error == null || !("json" in error)) {
    return {};
  }

  const maybeJson = error.json;

  if (typeof maybeJson != "function") {
    return {};
  }

  try {
    const payload = (await maybeJson.call(error)) as unknown;
    return typeof payload == "object" && payload != null ? (payload as DeliveryErrorPayload) : {};
  } catch {
    return {};
  }
}

async function handleDeliveryError(
  dispatch: AppDispatch,
  type: DeliveryErrorActionType,
  notification: TUserActionNotification,
  error: unknown,
) {
  dispatch({ type });

  const payload = await parseDeliveryError(error);
  dispatch(
    addUserActionNotification({ ...notification, message: getDeliveryErrorMessage(payload) }),
  );
}

export function getDeliveries(patient_id: TPatient["patient_id"]) {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: GET_DELIVERIES });
    try {
      const response = await fetchWithAuth(API_DELIVERIES(patient_id), { method: "GET" });
      if (!response.ok) {
        throw new Error("Error occurred trying to fetch deliveries");
      }
      const fields = await response.json();
      dispatch({ type: GET_DELIVERIES_SUCCESS, fields });
    } catch (e) {
      dispatch({ type: GET_DELIVERIES_ERROR, e });
    }
  };
}

export function getShipment(patient_id: TPatient["patient_id"]) {
  const NOTIFICATION_MSG_ERROR: TUserActionNotification = {
    message: "notify.doctor.update.error",
    level: "error",
    position: "tl",
    autoDismiss: 4,
  };

  return (dispatch: AppDispatch) => {
    dispatch({ type: CREATE_SHIPMENT });
    fetchWithAuth(API_DELIVERY_SHIPMENT, {
      method: "POST",
      body: JSON.stringify({ patient_id }),
    })
      .then((response) => {
        if (!response.ok) {
          return Promise.reject(new DeliveryRequestError(response));
        }
        return response;
      })
      .then((response) => response.json())
      .then((fields) => {
        dispatch({ type: CREATE_SHIPMENT_SUCCESS, fields });
      })
      .catch((error) => {
        void handleDeliveryError(dispatch, CREATE_SHIPMENT_ERROR, NOTIFICATION_MSG_ERROR, error);
      });
  };
}

export function pickupByPhone(
  patient_id: TPatient["patient_id"],
  delivery_id: TDelivery["delivery_id"],
) {
  const NOTIFICATION_MSG_ERROR: TUserActionNotification = {
    message: "notify.doctor.update.error",
    level: "error",
    position: "tl",
    autoDismiss: 4,
  };

  return (dispatch: AppDispatch) => {
    dispatch({ type: PICKUP });
    fetchWithAuth(API_DELIVERY_PICKUP_BY_PHONE, {
      method: "POST",
      body: JSON.stringify({ patient_id, delivery_id }),
    })
      .then((response) => {
        if (!response.ok) {
          return Promise.reject(new DeliveryRequestError(response));
        }
        return response;
      })
      .then((response) => response.json())
      .then((_) => {
        dispatch({ type: PICKUP_SUCCESS });
        void dispatch(getPatientId(patient_id));
      })
      .catch((error) => {
        void handleDeliveryError(dispatch, PICKUP_ERROR, NOTIFICATION_MSG_ERROR, error);
      });
  };
}

export function pickup(patient_id: TPatient["patient_id"], delivery_id: TDelivery["delivery_id"]) {
  const NOTIFICATION_MSG_ERROR: TUserActionNotification = {
    message: "notify.doctor.update.error",
    level: "error",
    position: "tl",
    autoDismiss: 4,
  };

  return (dispatch: AppDispatch) => {
    dispatch({ type: PICKUP });
    fetchWithAuth(API_DELIVERY_PICKUP, {
      method: "POST",
      body: JSON.stringify({ patient_id, delivery_id }),
    })
      .then((response) => {
        if (!response.ok) {
          return Promise.reject(new DeliveryRequestError(response));
        }
        return response;
      })
      .then((response) => response.json())
      .then((_) => {
        dispatch({ type: PICKUP_SUCCESS });
        void dispatch(getPatientId(patient_id, /*fetchDeliveries:*/ true));
      })
      .catch((error) => {
        void handleDeliveryError(dispatch, PICKUP_ERROR, NOTIFICATION_MSG_ERROR, error);
      });
  };
}

export function getAllShipments(from: string | null = null) {
  const f = `start=${from}`;
  let url = `${API_SHIPMENTS}`;

  if (from !== null) {
    url = `${API_SHIPMENTS}?${f}`;
  }

  const NOTIFICATION_MSG_ERROR: TUserActionNotification = {
    message: "notify.shipments.error",
    level: "error",
    position: "tl",
    autoDismiss: 4,
  };

  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetchWithAuth(url);
      const json = await response.json();

      if (!response.ok && !isSignedOfferRequiredError(json)) {
        throw new Error("Error occurred trying to fetch shipments");
      }

      dispatch({ type: "SHIPMENTS_SUCCESS", json });
    } catch {
      dispatch(addUserActionNotification(NOTIFICATION_MSG_ERROR));
    }
  };
}
